import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApplyTenantTheme } from '@mlm/ui/lib/tenant-theme';
import { getPublicTenantConfig, tenantLogoUrl } from './api';
import { railCssVars, railIsDark, tillTheme } from './pos-theme';

/**
 * The cashier's half of tenant theming, mirroring the member app's
 * `theme-boot.tsx`: read the tenant's theme and hand it to the shared applier
 * in `@mlm/ui`, which owns the DOM work.
 *
 * Reuses the `['public-tenant-config']` query the sidebar and the coin label
 * already share, so this adds no request. The endpoint is public, which is
 * what lets the login screen be themed before anyone has signed in.
 *
 * LIGHT ONLY: the till has no dark set of its own, so the mode is never passed
 * and a tenant's `theme.dark` is ignored here.
 *
 * The till applies `tillTheme(theme)`, not the theme itself: a tenant still on
 * the default store orange keeps the till's own Omni look (see `tillTheme`).
 */
export function useTenantTheme() {
  const { data } = useQuery({
    queryKey: ['public-tenant-config'],
    queryFn: getPublicTenantConfig,
    staleTime: 60_000,
    retry: false,
  });
  // Memoised on the server answer: a fresh object every render would re-run the
  // two effects below on every render.
  const theme = useMemo(() => tillTheme(data?.theme ?? null), [data?.theme]);

  // Same precedence as the member portal, minus its per-hostname override.
  const faviconUrl = theme?.faviconKey
    ? `${tenantLogoUrl('favicon')}?v=${encodeURIComponent(theme.faviconKey)}`
    : theme?.logoKey
      ? `${tenantLogoUrl('light')}?v=${encodeURIComponent(theme.logoKey)}`
      : (theme?.logoUrl ?? null);

  useApplyTenantTheme({ theme, faviconUrl });

  // The rail colours are the till's own tokens, so the shared applier does not
  // know them (see `railCssVars` for why they cannot be derived in CSS).
  useEffect(() => {
    const root = document.documentElement;
    const vars = railCssVars(theme);
    for (const [name, value] of vars) root.style.setProperty(name, value);
    return () => {
      for (const [name] of vars) root.style.removeProperty(name);
    };
  }, [theme]);
}

/**
 * The tenant logo for the rail and the login screen, or null when the tenant
 * has none (callers fall back to the tenant name). An uploaded logo wins over
 * a pasted URL, as everywhere else.
 */
export function useTenantBrand(): { name: string; logoUrl: string | null } {
  const { data } = useQuery({
    queryKey: ['public-tenant-config'],
    queryFn: getPublicTenantConfig,
    staleTime: 60_000,
    retry: false,
  });
  const theme = data?.theme;
  const light = theme?.logoKey
    ? `${tenantLogoUrl('light')}?v=${encodeURIComponent(theme.logoKey)}`
    : (theme?.logoUrl ?? null);
  const dark = theme?.logoKeyDark
    ? `${tenantLogoUrl('dark')}?v=${encodeURIComponent(theme.logoKeyDark)}`
    : (theme?.logoUrlDark ?? null);
  // The two surfaces that show it (rail, login backdrop) share one colour, so
  // one answer serves both: the dark-surface logo on a dark rail, falling back
  // to the other variant when the tenant uploaded only one.
  const logoUrl = railIsDark(tillTheme(theme)) ? (dark ?? light) : (light ?? dark);
  // A tenant's short wordmark ("FLX") reads better than its full legal name
  // wherever the logo is missing.
  return { name: theme?.wordmark?.trim() || data?.name || 'Cashier POS', logoUrl };
}
